import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  Brain,
  Award,
  Target,
  HelpCircle,
  Zap,
} from "lucide-react";
import Layout from "../../layout";
import { useAuth } from "../../authContext";

const StressAssessmentDetail = ({ onBack, onComplete }) => {
  const { url, headers } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${url}/assessment/questions?testtype=stress`,
          {
            method: "GET",
            headers: headers,
          }
        );

        if (response.ok) {
          const apiResponse = await response.json();
          const data = apiResponse.data;
          const transformedQuestions = data.questions.map((q) => ({
            id: q.id,
            category: q.category,
            question: q.question,
            options: [
              {
                text: q.option1_text,
                score: Number(q.option1_score) || 0,
                explanation: q.option1_explanation,
              },
              {
                text: q.option2_text,
                score: Number(q.option2_score) || 0,
                explanation: q.option2_explanation,
              },
              {
                text: q.option3_text,
                score: Number(q.option3_score) || 0,
                explanation: q.option3_explanation,
              },
              {
                text: q.option4_text,
                score: Number(q.option4_score) || 0,
                explanation: q.option4_explanation,
              },
              {
                text: q.option5_text,
                score: Number(q.option5_score) || 0,
                explanation: q.option5_explanation,
              },
            ].filter((opt) => opt.text && opt.text.trim() !== ""), // Remove empty options
          }));

          setQuestions(transformedQuestions);
          /*console.log(
            "✅ Stress questions fetched successfully:",
            transformedQuestions.length
          );*/
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.error("❌ Error fetching stress questions:", error);
        setError("Failed to load questions. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [url, headers]);

  const handleAnswer = (score) => {
    const newAnswers = [...answers, score];
    setAnswers(newAnswers);

    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      calculateResults(newAnswers);
    }
  };

  const calculateResults = (allAnswers) => {
    const totalScore = allAnswers.reduce((sum, score) => sum + score, 0);
    const maxScore = questions.length * 4;
    const percentage = Math.round((totalScore / maxScore) * 100);

    let stressLevel;
    if (totalScore >= 0 && totalScore <= 12) {
      stressLevel = "minimal";
    } else if (totalScore >= 13 && totalScore <= 24) {
      stressLevel = "mild";
    } else if (totalScore >= 25 && totalScore <= 36) {
      stressLevel = "moderate";
    } else if (totalScore >= 37 && totalScore <= 42) {
      stressLevel = "high";
    } else {
      stressLevel = "severe";
    }

    const stressData = {
      minimal: {
        level: "Minimal Stress",
        color: "from-green-400 to-green-600",
        icon: "😌",
        description:
          "You're managing stress very well and maintaining good mental health.",
        recommendations: [
          "Continue your current stress management practices",
          "Maintain regular exercise and healthy sleep habits",
          "Keep nurturing your social relationships",
          "Practice gratitude and mindfulness regularly",
          "Share your coping strategies with others who might benefit",
        ],
        emergencyLevel: false,
      },
      mild: {
        level: "Mild Stress",
        color: "from-blue-400 to-blue-600",
        icon: "😊",
        description:
          "You're experiencing some stress but it's within normal range and manageable.",
        recommendations: [
          "Implement regular relaxation techniques like deep breathing",
          "Maintain a consistent sleep schedule of 7-9 hours",
          "Exercise regularly to help manage stress hormones",
          "Practice time management and organization skills",
          "Consider learning new stress management techniques",
        ],
        emergencyLevel: false,
      },
      moderate: {
        level: "Moderate Stress",
        color: "from-yellow-400 to-yellow-600",
        icon: "😐",
        description:
          "You're experiencing significant stress that may be impacting your daily life.",
        recommendations: [
          "Start practicing daily stress reduction techniques",
          "Consider talking to a counselor or therapist",
          "Evaluate your workload and commitments - consider reducing if possible",
          "Prioritize self-care activities and personal time",
          "Reach out to friends, family, or support groups",
          "Consider mindfulness meditation or yoga classes",
        ],
        emergencyLevel: false,
      },
      high: {
        level: "High Stress",
        color: "from-orange-400 to-orange-600",
        icon: "😟",
        description:
          "You're experiencing high levels of stress that require immediate attention.",
        recommendations: [
          "Seek professional counseling or therapy services immediately",
          "Contact your school's counseling center or mental health services",
          "Consider temporarily reducing academic or work commitments",
          "Focus on essential needs: sleep, nutrition, and basic self-care",
          "Reach out to trusted friends, family members, or mentors for support",
          "Consider stress management workshops or support groups",
          "Speak with a healthcare provider about your symptoms",
        ],
        emergencyLevel: true,
      },
      severe: {
        level: "Severe Stress",
        color: "from-red-400 to-red-600",
        icon: "😰",
        description:
          "You're experiencing severe stress levels that may be affecting your health and functioning.",
        recommendations: [
          "Contact mental health services or crisis support immediately",
          "Speak with a healthcare provider or counselor as soon as possible",
          "Consider contacting a crisis helpline if you feel overwhelmed",
          "Inform trusted family members or friends about your stress levels",
          "Focus only on essential daily activities and basic self-care",
          "Consider taking a temporary break from major stressors if possible",
          "Don't try to handle this alone - seek professional help immediately",
        ],
        emergencyLevel: true,
      },
    };

    const levelData = stressData[stressLevel];

    const categoryScores = {};
    const categories = [
      "Physical Symptoms",
      "Sleep Quality",
      "Emotional Regulation",
      "Academic Performance",
      "Social Relationships",
      "Coping Mechanisms",
      "Physical Energy",
      "Time Management",
      "Appetite and Eating",
      "Future Outlook",
      "Stress Response",
      "Life Satisfaction",
    ];

    categories.forEach((category, index) => {
      categoryScores[category] = allAnswers[index] || 0;
    });

    const domainScores = {
      "Physical Wellbeing":
        (categoryScores["Physical Symptoms"] +
          categoryScores["Sleep Quality"] +
          categoryScores["Physical Energy"]) /
        3,
      "Emotional Regulation":
        (categoryScores["Emotional Regulation"] +
          categoryScores["Stress Response"] +
          categoryScores["Future Outlook"]) /
        3,
      "Academic/Work Performance":
        (categoryScores["Academic Performance"] +
          categoryScores["Time Management"]) /
        2,
      "Social & Lifestyle":
        (categoryScores["Social Relationships"] +
          categoryScores["Coping Mechanisms"] +
          categoryScores["Appetite and Eating"] +
          categoryScores["Life Satisfaction"]) /
        4,
    };

    const managementTips = {
      immediate: [
        "Take 5 deep breaths using the 4-7-8 technique",
        "Step outside for fresh air and a brief walk",
        "Do progressive muscle relaxation for 2-3 minutes",
        "Practice the 5-4-3-2-1 grounding technique",
        "Listen to calming music or nature sounds",
      ],
      daily: [
        "Maintain a consistent sleep schedule of 7-9 hours",
        "Exercise for at least 30 minutes daily",
        "Practice mindfulness meditation for 10-15 minutes",
        "Limit caffeine and alcohol consumption",
        "Keep a stress journal to identify patterns",
        "Schedule regular breaks throughout the day",
      ],
      longTerm: [
        "Develop healthy boundaries with work and commitments",
        "Build a strong support network of friends and family",
        "Learn time management and organizational skills",
        "Consider therapy or counseling for persistent stress",
        "Practice regular self-care activities you enjoy",
        "Develop hobbies and interests outside of work/school",
      ],
    };

    const resultData = {
      score: totalScore,
      maxScore,
      percentage,
      level: levelData.level,
      message: levelData.description,
      recommendations: levelData.recommendations,
      color: levelData.color,
      icon: levelData.icon,
      emergencyLevel: levelData.emergencyLevel,
      categoryScores,
      domainScores,
      managementTips,
      completedAt: new Date().toISOString(),
    };

    setResults(resultData);
    setShowResults(true);
    onComplete(resultData);
  };

  const resetAssessment = () => {
    setCurrentStep(0);
    setAnswers([]);
    setResults(null);
    setShowResults(false);
  };

  const progress =
    questions.length > 0 ? ((currentStep + 1) / questions.length) * 100 : 0;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen p-4 sm:p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">🧠</div>
            <div className="text-xl font-semibold text-gray-800 mb-2">
              Loading Stress Assessment...
            </div>
            <div className="text-gray-600">Preparing your questions</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen p-4 sm:p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">❌</div>
            <div className="text-xl font-semibold text-red-600 mb-2">
              Error Loading Questions
            </div>
            <div className="text-gray-600 mb-4">{error}</div>
            <button
              onClick={onBack}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (questions.length === 0) {
    return (
      <Layout>
        <div className="min-h-screen p-4 sm:p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">📝</div>
            <div className="text-xl font-semibold text-gray-800 mb-2">
              No Questions Available
            </div>
            <div className="text-gray-600 mb-4">Please try again later</div>
            <button
              onClick={onBack}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen p-4 sm:p-6">
        <div className="max-w-8xl mx-auto">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={onBack}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4 sm:mb-6 font-medium text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            Back to Mental Health Hub
          </motion.button>

          {!showResults ? (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 text-white mb-6 sm:mb-8"
              >
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 lg:gap-6">
                  <div className="text-5xl sm:text-6xl lg:text-7xl">🧠</div>
                  <div className="text-center sm:text-left">
                    <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-1 sm:mb-2">
                      Stress Level Assessment
                    </h1>
                    <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-purple-100 mb-1 sm:mb-2">
                      Understand your current stress levels
                    </p>
                    <div className="bg-white/20 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium inline-block">
                      Question {currentStep + 1} of {questions.length}
                    </div>
                  </div>
                </div>

                <div className="w-full bg-white/20 rounded-full h-2 sm:h-3 mt-4 sm:mt-6">
                  <div
                    className="bg-white h-2 sm:h-3 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </motion.div>

              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-lg sm:rounded-xl shadow-lg p-4 sm:p-6 lg:p-8"
              >
                <div className="mb-4">
                  <span className="inline-block px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                    {questions[currentStep].category}
                  </span>
                </div>

                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-4 sm:mb-6 text-gray-900">
                  {questions[currentStep].question}
                </h2>

                <div className="space-y-3 sm:space-y-4">
                  {questions[currentStep].options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswer(option.score)}
                      className="w-full text-left p-3 sm:p-4 lg:p-6 rounded-lg border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all text-sm sm:text-base group"
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-gray-400 group-hover:border-purple-500 rounded-full flex-shrink-0 transition-colors"></div>
                        <div className="flex-1">
                          <span className="block">{option.text}</span>
                          {option.explanation && (
                            <span className="text-xs text-gray-500 mt-1 block">
                              {option.explanation}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="flex justify-between items-center mt-6 sm:mt-8">
                  <button
                    onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                    disabled={currentStep === 0}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    Previous
                  </button>
                  <div className="text-xs sm:text-sm text-gray-500">
                    {currentStep + 1} of {questions.length}
                  </div>
                </div>
              </motion.div>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6 sm:space-y-8"
            >
              {results.emergencyLevel && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg sm:rounded-xl p-4 sm:p-6">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-bold text-red-800 mb-2 text-lg">
                        ⚠️ High Stress Level Detected
                      </h3>
                      <p className="text-red-700 text-sm sm:text-base mb-4">
                        Your stress levels indicate you may need immediate
                        support. Please consider reaching out to a CubeCover
                        Mental Health Therapist for assistance.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <a
                          href="https://wa.me/2349160001429?text=Hi, I just completed a stress assessment and my results indicate I may need support. Can you help me?"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base font-medium"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                          </svg>
                          Contact via WhatsApp
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div
                className={`bg-gradient-to-r ${results.color} rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 text-white text-center`}
              >
                <div className="text-5xl sm:text-6xl lg:text-7xl mb-4">
                  {results.icon}
                </div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2">
                  Assessment Complete!
                </h1>
                <p className="text-sm sm:text-base lg:text-lg opacity-90 mb-4">
                  Here are your personalized results
                </p>
                <div className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">
                  {results.percentage}%
                </div>
                <div className="text-lg sm:text-xl lg:text-2xl font-semibold">
                  {results.level}
                </div>
              </div>

              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-4 sm:p-6 lg:p-8">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  What This Means
                </h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                  <p className="text-blue-800 text-sm sm:text-base leading-relaxed">
                    {results.message}
                  </p>
                </div>

                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-600" />
                  Recommended Actions
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  {results.recommendations.map((rec, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-green-50 rounded-lg"
                    >
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm sm:text-base text-gray-800">
                        {rec}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {(results.emergencyLevel || results.percentage >= 50) && (
                <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-4 sm:p-6 lg:p-8">
                  <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                    Stress Relief Tools for You
                  </h2>
                  <p className="text-gray-600 mb-4 text-sm sm:text-base">
                    Your stress levels suggest immediate action. Try these
                    proven stress management techniques:
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div
                      onClick={() => {
                        const breathingActivity = {
                          id: "breathing-patterns",
                          title: "4-7-8 Breathing for Stress",
                          description:
                            "Rapid stress relief through controlled breathing",
                          icon: "🫁",
                          category: "Breathing",
                          duration: "3-10 minutes",
                          techniques: [
                            {
                              name: "4-7-8 Breathing",
                              pattern: [4, 7, 8, 0],
                              purpose:
                                "Reduces stress hormones and activates relaxation response instantly",
                            },
                            {
                              name: "Stress Relief Breathing",
                              pattern: [4, 4, 6, 2],
                              purpose:
                                "Quick technique for immediate stress reduction",
                            },
                          ],
                        };
                        localStorage.setItem(
                          "selectedActivity",
                          JSON.stringify(breathingActivity)
                        );
                        window.location.href =
                          "/therapy-tool?activity=breathing-patterns";
                      }}
                      className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">🫁</div>
                        <div>
                          <h3 className="font-semibold text-blue-800 mb-1">
                            4-7-8 Breathing Exercise
                          </h3>
                          <p className="text-xs text-blue-600">
                            Scientifically proven technique to reduce stress
                            hormones instantly
                          </p>
                        </div>
                      </div>
                    </div>

                    <div
                      onClick={() => {
                        const soundActivity = {
                          id: "nature-sounds",
                          title: "Calming Nature Sounds",
                          description:
                            "Natural sounds for immediate stress relief",
                          icon: "🌱",
                          category: "Sound Therapy",
                          duration: "As needed",
                          sounds: [
                            {
                              name: "Rain",
                              emoji: "🌧️",
                              file: "/sounds/rain.mp3",
                            },
                            {
                              name: "Ocean Waves",
                              emoji: "🌊",
                              file: "/sounds/ocean.mp3",
                            },
                            {
                              name: "Forest",
                              emoji: "🌲",
                              file: "/sounds/forest.mp3",
                            },
                            {
                              name: "Thunderstorm",
                              emoji: "⛈️",
                              file: "/sounds/thunder.mp3",
                            },
                          ],
                        };
                        localStorage.setItem(
                          "selectedActivity",
                          JSON.stringify(soundActivity)
                        );
                        window.location.href =
                          "/therapy-tool?activity=nature-sounds";
                      }}
                      className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">🌱</div>
                        <div>
                          <h3 className="font-semibold text-green-800 mb-1">
                            Calming Nature Sounds
                          </h3>
                          <p className="text-xs text-green-600">
                            Immerse in peaceful sounds that naturally lower
                            stress levels
                          </p>
                        </div>
                      </div>
                    </div>

                    <div
                      onClick={() => {
                        const emotionActivity = {
                          id: "emotional-regulation",
                          title: "Stress Management Tools",
                          description:
                            "Techniques to process and release stress",
                          icon: "🧘",
                          category: "Emotional Wellness",
                          duration: "5-15 minutes",
                          tools: [
                            {
                              name: "Progressive Muscle Relaxation",
                              steps: [
                                "Tense muscle groups",
                                "Hold for 5 seconds",
                                "Release and relax",
                                "Notice the contrast",
                              ],
                            },
                          ],
                        };
                        localStorage.setItem(
                          "selectedActivity",
                          JSON.stringify(emotionActivity)
                        );
                        window.location.href =
                          "/therapy-tool?activity=emotional-regulation";
                      }}
                      className="bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">🧘</div>
                        <div>
                          <h3 className="font-semibold text-purple-800 mb-1">
                            Progressive Muscle Relaxation
                          </h3>
                          <p className="text-xs text-purple-600">
                            Release physical tension and promote deep stress
                            relief
                          </p>
                        </div>
                      </div>
                    </div>

                    <div
                      onClick={() => {
                        const colorActivity = {
                          id: "color-therapy",
                          title: "Stress Relief Color Therapy",
                          description: "Visual meditation to reduce stress",
                          icon: "🎨",
                          category: "Visual Therapy",
                          duration: "5-10 minutes",
                          colors: [
                            {
                              name: "Blue",
                              hex: "#4A90E2",
                              effect:
                                "Lowers blood pressure and reduces stress hormones",
                            },
                            {
                              name: "Green",
                              hex: "#7ED321",
                              effect:
                                "Promotes balance and emotional stability",
                            },
                            {
                              name: "Lavender",
                              hex: "#9B59B6",
                              effect: "Enhances relaxation and peace of mind",
                            },
                          ],
                        };
                        localStorage.setItem(
                          "selectedActivity",
                          JSON.stringify(colorActivity)
                        );
                        window.location.href =
                          "/therapy-tool?activity=color-therapy";
                      }}
                      className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-all"
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">�</div>
                        <div>
                          <h3 className="font-semibold text-indigo-800 mb-1">
                            Stress Relief Visualization
                          </h3>
                          <p className="text-xs text-indigo-600">
                            Use color therapy to calm your mind and reduce
                            stress
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  onClick={resetAssessment}
                  className="flex-1 py-2 sm:py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base"
                >
                  Take Assessment Again
                </button>
                <button
                  onClick={onBack}
                  className="flex-1 py-2 sm:py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
                >
                  Back to Mental Health Hub
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StressAssessmentDetail;
